/**
 * UpgradeLimitModal (Story 2.9, Task 1)
 *
 * Reusable modal displayed when a user hits a plan limit.
 * Shows human-readable feature name, upgrade pricing, feature highlights,
 * and accessible CTA buttons.
 *
 * Props:
 * - feature: Which plan feature was blocked
 * - currentPlan, currentUsage, currentLimit: Current state
 * - upgradePlan, nextTierLimit: Target upgrade info
 * - billingInterval: "monthly" | "annual" for correct pricing
 * - open, onOpenChange: Dialog state
 */

import { useState, useCallback } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
} from "@provenleap/ui";
import {
  PLAN_LIMITS,
  PLAN_PRICING,
  PLAN_DISPLAY_NAMES,
  FEATURE_DISPLAY_NAMES,
  formatLimit,
} from "@provenleap/types";
import type { Plan, PlanFeature } from "@provenleap/types";

const PRICING = PLAN_PRICING;

/**
 * Key feature highlights per plan for the upgrade pitch.
 * Numeric limits pull from PLAN_LIMITS so this stays in sync with the source of truth.
 */
const PLAN_QUALITATIVE_HIGHLIGHTS: Record<Exclude<Plan, "free">, string[]> = {
  starter: ["12 months of history", "Standard AI insights"],
  pro: ["24 months of history", "Advanced AI insights"],
  agency: ["60 months of history", "Full white-label branding"],
};

function getPlanHighlights(plan: Exclude<Plan, "free">): string[] {
  const limits = PLAN_LIMITS[plan];
  const teamMembersLabel =
    limits.teamMembers === null
      ? "Unlimited team members"
      : `${limits.teamMembers} team members`;
  return [
    `Up to ${limits.clientLimit} clients`,
    teamMembersLabel,
    ...PLAN_QUALITATIVE_HIGHLIGHTS[plan],
  ];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface UpgradeLimitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: PlanFeature;
  currentPlan: Plan;
  currentUsage: number;
  currentLimit: number;
  upgradePlan?: Plan;
  nextTierLimit: number | null;
  billingInterval: "monthly" | "annual";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UpgradeLimitModal({
  open,
  onOpenChange,
  feature,
  currentPlan,
  currentUsage: _currentUsage,
  currentLimit,
  upgradePlan,
  nextTierLimit,
  billingInterval,
}: UpgradeLimitModalProps) {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const featureName = FEATURE_DISPLAY_NAMES[feature];
  const limitDisplay = formatLimit(nextTierLimit);
  const isTopTier = !upgradePlan;

  const priceDisplay =
    upgradePlan && upgradePlan !== "free"
      ? billingInterval === "annual"
        ? `$${PRICING[upgradePlan as Exclude<Plan, "free">].effectiveMonthly}/mo`
        : `$${PRICING[upgradePlan as Exclude<Plan, "free">].monthly}/mo`
      : null;

  const annualNote =
    upgradePlan && upgradePlan !== "free" && billingInterval === "annual"
      ? " billed annually"
      : "";

  const highlights =
    upgradePlan && upgradePlan !== "free"
      ? getPlanHighlights(upgradePlan as Exclude<Plan, "free">)
      : [];

  const handleUpgrade = useCallback(async () => {
    if (!upgradePlan || isNavigating) return;
    setIsNavigating(true);
    setError(null);
    try {
      await navigate({
        to: "/settings/billing",
        search: { upgrade: "true", plan: upgradePlan },
      });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsNavigating(false);
    }
  }, [upgradePlan, navigate, isNavigating]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isTopTier
              ? `${featureName} limit reached`
              : `Upgrade to ${PLAN_DISPLAY_NAMES[upgradePlan!]}`}
          </DialogTitle>
          <DialogDescription>
            {isTopTier
              ? `You've reached the ${currentLimit}-${featureName} limit on ${PLAN_DISPLAY_NAMES[currentPlan]}. Contact support for higher limits.`
              : `You've reached the ${currentLimit}-${featureName} limit on ${PLAN_DISPLAY_NAMES[currentPlan]}. Upgrade to ${PLAN_DISPLAY_NAMES[upgradePlan!]} (${priceDisplay}${annualNote}) for up to ${limitDisplay} ${featureName}${nextTierLimit === 1 ? "" : "s"}.`}
          </DialogDescription>
        </DialogHeader>

        {/* Feature highlights */}
        {!isTopTier && highlights.length > 0 && (
          <div className="py-3">
            <p className="text-sm font-medium mb-2">
              What you get with {PLAN_DISPLAY_NAMES[upgradePlan!]}:
            </p>
            <ul className="space-y-1">
              {highlights.map((h) => (
                <li key={h} className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="text-green-500">&#10003;</span>
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Error message */}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        {/* Compare plans link */}
        <div className="text-center">
          <Link
            to="/settings/billing"
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            Compare Plans
          </Link>
        </div>

        <DialogFooter>
          {isTopTier ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Maybe Later
              </Button>
              <Button onClick={handleUpgrade} disabled={isNavigating}>
                {isNavigating ? "Redirecting..." : "Upgrade Now"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
